import { css, cva } from '@styled-system/css';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { type FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import type { CascaderOption } from '@/shared/types/options';

interface MobileCategoryDrilldownProps {
  options: CascaderOption[];
  /** Current selected paths (each path = ordered list of node values from root to selected node, at any depth). */
  value: string[][];
  onChange: (paths: string[][]) => void;
  /** Arbre catégories en cours de chargement réseau — affiche un indicateur plutôt que "aucune catégorie". */
  loading?: boolean;
  /** Libellé du déclencheur sans sélection (défaut : « Sélectionner une catégorie »). */
  placeholder?: string;
}

// ─── Tree helpers ───────────────────────────────────────────────────────

const buildNodeIndex = (tree: CascaderOption[]): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  const walk = (nodes: CascaderOption[], path: string[]) => {
    for (const node of nodes) {
      const newPath = [...path, node.value];
      map.set(node.value, newPath);
      if (node.children?.length) walk(node.children, newPath);
    }
  };
  walk(tree, []);
  return map;
};

const childrenAtPath = (tree: CascaderOption[], path: string[]): CascaderOption[] => {
  let level = tree;
  for (const seg of path) {
    const found = level.find((n) => n.value === seg);
    if (!found || !found.children) return [];
    level = found.children;
  }
  return level;
};

const breadcrumbLabels = (tree: CascaderOption[], path: string[]): string[] => {
  const labels: string[] = [];
  let level = tree;
  for (const seg of path) {
    const found = level.find((n) => n.value === seg);
    if (!found) break;
    labels.push(found.label);
    if (!found.children) break;
    level = found.children;
  }
  return labels;
};

/** Build `Map<nodeValue, ownLabel>` for every node (any depth). */
const buildLabelIndex = (tree: CascaderOption[]): Map<string, string> => {
  const map = new Map<string, string>();
  const walk = (nodes: CascaderOption[]) => {
    for (const node of nodes) {
      map.set(node.value, node.label);
      if (node.children?.length) walk(node.children);
    }
  };
  walk(tree);
  return map;
};

const flattenAll = (tree: CascaderOption[]): { value: string; label: string; hasChildren: boolean }[] => {
  const out: { value: string; label: string; hasChildren: boolean }[] = [];
  const walk = (nodes: CascaderOption[], labels: string[]) => {
    for (const node of nodes) {
      const newLabels = [...labels, node.label];
      const hasChildren = !!node.children?.length;
      out.push({ value: node.value, label: newLabels.join(' › '), hasChildren });
      if (hasChildren) walk(node.children!, newLabels);
    }
  };
  walk(tree, []);
  return out;
};

// ─── Trigger button (inline, in the accordion) ─────────────────────────

const trigger = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  width: '100%',
  paddingY: '4',
  paddingX: '4',
  borderRadius: 'md',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.strong',
  bg: 'surface.default',
  color: 'text.primary',
  fontFamily: 'sans',
  fontSize: 'bodySm',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 120ms ease, background 120ms ease',
  _hover: { borderColor: 'border.focus' },
});

const triggerLabel = css({
  flex: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'text.tertiary',
});

const triggerTagsWrap = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '2',
  flex: 1,
  minWidth: 0,
  alignItems: 'center',
});

const triggerTag = css({
  display: 'inline-flex',
  alignItems: 'center',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'full',
  bg: 'brand.primarySoft',
  color: 'brand.primary',
  fontSize: 'caption',
  fontWeight: 'medium',
  letterSpacing: '-0.1px',
  maxWidth: '160px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const triggerCount = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '22px',
  height: '22px',
  paddingX: '2',
  borderRadius: 'full',
  bg: 'brand.primary',
  color: 'white',
  fontSize: 'caption',
  fontWeight: 'semibold',
  fontFamily: 'mono',
});

// ─── Fullscreen overlay ────────────────────────────────────────────────

const overlayBackdrop = css({
  position: 'fixed',
  inset: 0,
  bg: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1100,
  animation: 'fadeIn 160ms ease-out',
});

const overlayPanel = css({
  position: 'fixed',
  inset: 0,
  zIndex: 1101,
  display: 'flex',
  flexDirection: 'column',
  bg: 'surface.canvas',
  color: 'text.primary',
  fontFamily: 'sans',
  animation: 'slideInBottom 220ms ease-out',
});

const headerBar = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)',
  paddingBottom: '4',
  paddingX: '5',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  bg: 'surface.default',
  flexShrink: 0,
});

const headerTitle = css({
  fontSize: 'h2',
  fontWeight: 'bold',
  letterSpacing: '-0.4px',
  color: 'text.primary',
});

const closeBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: 'md',
  background: 'transparent',
  border: 'none',
  color: 'text.secondary',
  cursor: 'pointer',
  transition: 'background 120ms ease, color 120ms ease',
  _hover: { bg: 'surface.subtle', color: 'text.primary' },
});

const searchSection = css({
  paddingY: '4',
  paddingX: '5',
  flexShrink: 0,
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  bg: 'surface.default',
});

// La breadcrumbBar EST le bouton retour : toute la rangée est cliquable,
// hit area minimale 48px (Apple HIG), icône et label bien visibles.
const breadcrumbBar = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  width: '100%',
  minHeight: '52px',
  paddingY: '3',
  paddingX: '5',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  bg: 'surface.default',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'sans',
  flexShrink: 0,
  transition: 'background 120ms ease',
  _hover: { bg: 'surface.subtle' },
});

const backIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: 'md',
  bg: 'surface.subtle',
  color: 'text.primary',
  flexShrink: 0,
});

const backLabel = css({
  fontSize: 'body',
  fontWeight: 'semibold',
  color: 'text.primary',
  letterSpacing: '-0.2px',
  flexShrink: 0,
});

const breadcrumbText = css({
  fontSize: 'bodySm',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
  minWidth: 0,
});

const list = css({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '4',
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
});

const row = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    paddingY: '4',
    paddingX: '4',
    borderRadius: 'lg',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.default',
    bg: 'surface.default',
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease',
    width: 'full',
    textAlign: 'left',
    _hover: { bg: 'surface.subtle', borderColor: 'border.strong' },
  },
  variants: {
    selected: {
      true: { bg: 'brand.primarySoft', borderColor: 'brand.primary' },
      false: {},
    },
  },
});

const checkBox = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: 'sm',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    flexShrink: 0,
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  variants: {
    state: {
      checked: { bg: 'brand.primary', borderColor: 'brand.primary', color: 'white' },
      indeterminate: { bg: 'brand.primary', borderColor: 'brand.primary', color: 'white' },
      unchecked: { borderColor: 'border.strong', color: 'transparent' },
    },
  },
});

const itemLabel = css({
  flex: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: 'body',
  color: 'text.primary',
});

const itemCount = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  fontFamily: 'mono',
  flexShrink: 0,
});

const arrow = css({
  color: 'text.tertiary',
  flexShrink: 0,
});

const empty = css({
  paddingY: '12',
  paddingX: '4',
  textAlign: 'center',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const loadingBox = css({
  display: 'flex',
  justifyContent: 'center',
  paddingY: '12',
});

const footerBar = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  paddingTop: '4',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  paddingX: '5',
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: 'border.subtle',
  bg: 'surface.default',
  flexShrink: 0,
});

const validateBtn = css({
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '48px',
  paddingX: '6',
  borderRadius: 'lg',
  bg: 'brand.primary',
  color: 'white',
  border: 'none',
  fontSize: 'body',
  fontWeight: 'semibold',
  fontFamily: 'sans',
  cursor: 'pointer',
  transition: 'background 120ms ease',
  _hover: { bg: 'brand.primaryHover' },
});

const clearBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  height: '48px',
  paddingX: '4',
  background: 'transparent',
  border: 'none',
  color: 'text.tertiary',
  fontSize: 'caption',
  fontWeight: 'medium',
  cursor: 'pointer',
  borderRadius: 'md',
  _hover: { color: 'text.primary', bg: 'surface.subtle' },
});

// ─── Icons ──────────────────────────────────────────────────────────────

const CheckIcon: FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 8L7 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DashIcon: FC = () => (
  <svg width="12" height="2" viewBox="0 0 12 2" fill="none">
    <line x1="0" y1="1" x2="12" y2="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ─── Component ──────────────────────────────────────────────────────────

export const MobileCategoryDrilldown: FC<MobileCategoryDrilldownProps> = ({
  options,
  value,
  onChange,
  loading = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [navPath, setNavPath] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  // Reset navigation when overlay closes.
  useEffect(() => {
    if (!isOpen) {
      setNavPath([]);
      setQuery('');
    }
  }, [isOpen]);

  // Lock body scroll while overlay is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Escape to close.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const nodeIndex = useMemo(() => buildNodeIndex(options), [options]);
  const labelIndex = useMemo(() => buildLabelIndex(options), [options]);
  const flatAll = useMemo(() => flattenAll(options), [options]);

  /** Names of selected categories — shown as chips inside the trigger so the
   * user sees WHICH categories are picked, not just a count. */
  const selectedLabels = useMemo(() => {
    return value
      .map((p) => {
        const leaf = p[p.length - 1] ?? '';
        return { value: leaf, label: labelIndex.get(leaf) ?? '' };
      })
      .filter((entry) => entry.label);
  }, [value, labelIndex]);

  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return flatAll.filter((n) => n.label.toLowerCase().includes(trimmedQuery));
  }, [flatAll, isSearching, trimmedQuery]);

  const selectedSet = useMemo(() => {
    const set = new Set<string>();
    for (const path of value) {
      const last = path[path.length - 1];
      if (last) set.add(last);
    }
    return set;
  }, [value]);

  const indeterminateSet = useMemo(() => {
    const set = new Set<string>();
    for (const path of value) {
      for (let i = 0; i < path.length - 1; i++) {
        const seg = path[i];
        if (seg) set.add(seg);
      }
    }
    return set;
  }, [value]);

  const currentChildren = useMemo(() => childrenAtPath(options, navPath), [options, navPath]);

  const breadcrumb = useMemo(() => breadcrumbLabels(options, navPath).join(' › '), [options, navPath]);

  const toggleNode = (nodeValue: string) => {
    const path = nodeIndex.get(nodeValue);
    if (!path) return;
    if (selectedSet.has(nodeValue)) {
      onChange(value.filter((p) => p[p.length - 1] !== nodeValue));
    } else {
      onChange([...value, path]);
    }
  };

  const drillInto = (nodeValue: string) => {
    setNavPath([...navPath, nodeValue]);
  };

  const goBack = () => setNavPath(navPath.slice(0, -1));

  type RowNode = { value: string; label: string; hasChildren: boolean; childCount?: number };

  const renderRow = (node: RowNode) => {
    const checked = selectedSet.has(node.value);
    const indeterminate = !checked && indeterminateSet.has(node.value);
    const state = checked ? 'checked' : indeterminate ? 'indeterminate' : 'unchecked';

    const handleRowClick = () => {
      if (node.hasChildren) drillInto(node.value);
      else toggleNode(node.value);
    };

    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleNode(node.value);
    };

    return (
      <button key={node.value} type="button" className={row({ selected: checked })} onClick={handleRowClick}>
        <span
          role="button"
          tabIndex={0}
          aria-checked={checked}
          aria-label={t('searchModal.drilldown.selectAria', { label: node.label })}
          className={checkBox({ state })}
          onClick={handleCheckboxClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              toggleNode(node.value);
            }
          }}
        >
          {checked ? <CheckIcon /> : indeterminate ? <DashIcon /> : null}
        </span>
        <span className={itemLabel}>{node.label}</span>
        {node.childCount != null && node.childCount > 0 && <span className={itemCount}>{node.childCount}</span>}
        {node.hasChildren && <ChevronRight size={18} strokeWidth={1.75} className={arrow} />}
      </button>
    );
  };

  return (
    <>
      {/* Inline trigger — shown in the AddFiltreModal accordion */}
      <button type="button" className={trigger} onClick={() => setIsOpen(true)}>
        {selectedLabels.length === 0 ? (
          <span className={triggerLabel}>{placeholder ?? t('searchModal.categoryPlaceholder')}</span>
        ) : (
          <span className={triggerTagsWrap}>
            {selectedLabels.map((s) => (
              <span key={s.value} className={triggerTag}>
                {s.label}
              </span>
            ))}
          </span>
        )}
        {selectedLabels.length > 0 && <span className={triggerCount}>{selectedLabels.length}</span>}
        <ChevronRight size={16} strokeWidth={1.75} className={arrow} />
      </button>

      {/* Fullscreen overlay — portal'd to body */}
      {isOpen &&
        createPortal(
          <>
            <div className={overlayBackdrop} onClick={() => setIsOpen(false)} />
            <div className={overlayPanel} role="dialog" aria-modal="true" aria-label={t('searchModal.categories')}>
              {/* Header */}
              <div className={headerBar}>
                <span className={headerTitle}>{t('searchModal.categories')}</span>
                <button
                  type="button"
                  className={closeBtn}
                  onClick={() => setIsOpen(false)}
                  aria-label={t('common.close')}
                >
                  <X size={20} strokeWidth={1.75} />
                </button>
              </div>

              {/* Search */}
              <div className={searchSection}>
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('ui.categorySearchPlaceholder')}
                  leading={<Search size={14} strokeWidth={1.75} />}
                  autoFocus
                />
              </div>

              {/* Breadcrumb / back — toute la barre est cliquable */}
              {!isSearching && navPath.length > 0 && (
                <button type="button" className={breadcrumbBar} onClick={goBack} aria-label={t('common.back')}>
                  <span className={backIcon}>
                    <ChevronLeft size={20} strokeWidth={2} />
                  </span>
                  <span className={backLabel}>{t('common.back')}</span>
                  <span className={breadcrumbText}>{breadcrumb}</span>
                </button>
              )}

              {/* List */}
              <div className={list}>
                {isSearching ? (
                  searchResults.length === 0 ? (
                    <div className={empty}>{t('common.noResults')}</div>
                  ) : (
                    searchResults.map((n) => renderRow({ value: n.value, label: n.label, hasChildren: false }))
                  )
                ) : loading ? (
                  <div className={loadingBox}>
                    <Spinner size={18} />
                  </div>
                ) : currentChildren.length === 0 ? (
                  <div className={empty}>{t('ui.noCategories')}</div>
                ) : (
                  currentChildren.map((n) =>
                    renderRow({
                      value: n.value,
                      label: n.label,
                      hasChildren: !!n.children?.length,
                      childCount: n.children?.length ?? 0,
                    }),
                  )
                )}
              </div>

              {/* Footer */}
              <div className={footerBar}>
                {value.length > 0 && (
                  <button type="button" className={clearBtn} onClick={() => onChange([])}>
                    {t('searchModal.drilldown.clearAll')}
                  </button>
                )}
                <button type="button" className={validateBtn} onClick={() => setIsOpen(false)}>
                  {value.length > 0
                    ? t('searchModal.drilldown.validateWithCount', { count: value.length })
                    : t('searchModal.drilldown.validate')}
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};
